"use client";

import React, { useEffect, useRef, useState } from "react";
import * as RechartsPrimitive from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface AnimatedCounterProps {
  target: number;
  suffix?: string;
  decimals?: number;
  duration?: number;
  isVisible: boolean;
}
function AnimatedCounter({
  target,
  suffix = "",
  decimals = 0,
  duration = 2000,
  isVisible
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number | null = null;
    let animationFrameId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const easeOut = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);
      setCount(target * easeOut);
      if (percentage < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [target, duration, isVisible]);
  return <span className="font-default text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-primary">
      {count.toFixed(decimals)}
      <span className="text-primary">{suffix}</span>
    </span>;
}
const generateSparklineData = (base: number, variance: number, points: number = 20) => {
  let current = base;
  return Array.from({
    length: points
  }).map((_, i) => {
    current += (Math.random() - 0.3) * variance;
    return {
      month: `M${i}`,
      value: Math.max(0, current)
    };
  });
};
const statsData = [{
  id: "aum",
  label: "Assets Under Management",
  target: 4.2,
  suffix: "B",
  decimals: 1,
  chartData: generateSparklineData(3.5, 0.4)
}, {
  id: "portfolio",
  label: "Active Portfolio Companies",
  target: 12,
  suffix: "+",
  decimals: 0,
  chartData: generateSparklineData(8, 2)
}, {
  id: "throughput",
  label: "Network Throughput",
  target: 850,
  suffix: "T/s",
  decimals: 0,
  chartData: generateSparklineData(600, 100)
}];
const chartConfig = {
  value: {
    label: "Growth",
    color: "var(--primary)"
  }
};
export default function TheGlobalEconomicGraph() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="130432" id="future-facing-metrics" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-32 shadow-[inset_0px_0px_100px_rgba(0,0,0,0.8)]">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} aria-hidden="true" />

      {}
      <div className="pointer-events-none absolute top-0 left-1/4 h-96 w-96 -translate-y-1/2 rounded-full bg-primary/5 blur-[120px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 right-1/4 h-96 w-96 translate-y-1/2 rounded-full bg-primary/5 blur-[120px]" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-2xl">
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight mb-4 uppercase">
            Future-Facing Metrics
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            Quantifying the scale, velocity, and performance of the Eagle 47 holdings ecosystem and digital commerce infrastructure.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 lg:gap-24">
          {statsData.map((stat, i) => <div key={stat.id} data-index={i} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <AnimatedCounter target={stat.target} suffix={stat.suffix} decimals={stat.decimals} isVisible={isVisible} />
                <span className="text-muted-foreground font-mono uppercase tracking-widest text-xs">
                  {stat.label}
                </span>
              </div>

              <div className="h-24 w-full mt-2 relative">
                {}
                <div className="absolute inset-0 bg-primary/5 blur-xl rounded-full" />
                
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <RechartsPrimitive.ResponsiveContainer width="100%" height="100%">
                    <RechartsPrimitive.AreaChart data={stat.chartData}>
                      <defs>
                        <linearGradient id={`fill-${stat.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
                        </linearGradient>
                        <filter id={`glow-${stat.id}`} x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur stdDeviation="4" result="blur" />
                          <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                      </defs>
                      <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel hideIndicator />} />
                      <RechartsPrimitive.Area type="monotone" dataKey="value" stroke="var(--color-value)" fill={`url(#fill-${stat.id})`} strokeWidth={2} filter={`url(#glow-${stat.id})`} isAnimationActive={isVisible} animationDuration={2000} animationEasing="ease-out" />
                    </RechartsPrimitive.AreaChart>
                  </RechartsPrimitive.ResponsiveContainer>
                </ChartContainer>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}